// ListOverview.jsx

import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import supabase from '../supabaseClient';
import {
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  Tabs,
  Tab,
  Pagination,
  keyframes,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ShareIcon from '@mui/icons-material/Share';
import PaletteIcon from '@mui/icons-material/Palette';
import UserContext from '../contexts/userContext';
import { EmptyListsState, NoSearchResultsState } from './EmptyStates';

// Preset accent colors - updated to match new design
const ACCENT_COLORS = [
  null, // No color / default
  '#BB86FC', // Purple (primary accent)
  '#03DAC6', // Teal (secondary accent)
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#FF6B6B', // Coral red
  '#45B7D1', // Sky blue
  '#10b981', // Emerald
  '#f59e0b', // Amber
  '#ec4899', // Pink
  '#14b8a6', // Cyan
];


// Keyframe animations
const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pinBounce = keyframes`
  0% { transform: scale(1) rotate(0deg); }
  30% { transform: scale(1.3) rotate(-10deg); }
  50% { transform: scale(1.2) rotate(5deg); }
  70% { transform: scale(1.1) rotate(-3deg); }
  100% { transform: scale(1) rotate(0deg); }
`;

// Thumbnail Strip Component
function ThumbnailStrip({ thumbnails, recipeCount, theme }) {
  const validThumbnails = (thumbnails || []).filter(url => 
    url && typeof url === 'string' && (url.startsWith('http') || url.startsWith('/'))
  ).slice(0, 4);
  
  const count = recipeCount || 0;
  const isDark = theme.palette.mode === 'dark';
  
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        mt: 1.5,
        minHeight: 44,
      }}
    >
      {/* Thumbnails */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {validThumbnails.map((url, index) => (
          <Box
            key={index}
            component="img"
            src={url}
            alt=""
            onError={(e) => { e.target.style.display = 'none'; }}
            sx={{
              width: 44,
              height: 44,
              borderRadius: '10px',
              objectFit: 'cover',
              border: `2px solid ${isDark ? 'rgba(30, 30, 35, 1)' : 'rgba(255, 255, 255, 1)'}`,
              boxShadow: isDark 
                ? '0 2px 8px rgba(0,0,0,0.4)' 
                : '0 2px 6px rgba(0,0,0,0.1)',
              marginLeft: index === 0 ? 0 : '-12px',
              zIndex: 4 - index,
              transition: 'transform 0.2s ease',
            }}
          />
        ))}
      </Box>
      
      {/* Recipe count */}
      <Typography 
        sx={{ 
          fontSize: '11px',
          fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
          color: theme.palette.text.disabled,
          lineHeight: 1,
          letterSpacing: '-0.01em',
        }}
      >
        {count} {count === 1 ? 'recipe' : 'recipes'}
      </Typography>
    </Box>
  );
}

// Local storage key for accent colors
const ACCENT_COLORS_KEY = 'recipe-catalog-list-accent-colors';

function ListOverview({ ownedLists, sharedLists, onNewListCreated, onTogglePin, onUpdateList }) {
  const { user } = useContext(UserContext);
  const [newListName, setNewListName] = useState('');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [animatingPinId, setAnimatingPinId] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [accentColors, setAccentColors] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(ACCENT_COLORS_KEY)) || {};
    } catch {
      return {};
    }
  });
  const theme = useTheme();

  // State for search term and tab value
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // Reset animation state when lists change significantly
  useEffect(() => {
    if (ownedLists.length > 0 || sharedLists.length > 0) {
      const timer = setTimeout(() => setIsInitialLoad(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [ownedLists.length, sharedLists.length]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(1); // Reset pagination when changing tabs
  };

  // Determine if a search term is present
  const hasSearchTerm = searchTerm.trim() !== '';

  // Filter lists based on search term
  const filteredOwnedLists = ownedLists.filter((list) =>
    list.list_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSharedLists = sharedLists.filter((list) =>
    list.list_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Combine lists when searching
  const combinedLists = hasSearchTerm
    ? [...filteredOwnedLists, ...filteredSharedLists]
    : tabValue === 0
    ? filteredOwnedLists
    : filteredSharedLists;

  // Pagination
  const [page, setPage] = useState(1);
  const listsPerPage = 10;
  const totalPages = Math.ceil(combinedLists.length / listsPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const currentLists = combinedLists.slice(
    (page - 1) * listsPerPage,
    page * listsPerPage
  );

  // Function to create a new list
  const handleCreateList = async () => {
    if (newListName.trim() === '') return;

    const { data, error } = await supabase
      .from('saved_lists')
      .insert([
        {
          list_name: newListName,
          user_id: user.id,
        },
      ])
      .select();

    if (error) {
      console.error('Error creating new list:', error);
    } else if (data && data.length > 0) {
      setNewListName('');
      onNewListCreated(data[0]);
    }
  };

  // Handle pin toggle with animation
  const handlePinClick = (listId, currentPinnedStatus) => {
    setAnimatingPinId(listId);
    onTogglePin(listId, currentPinnedStatus);
    setTimeout(() => setAnimatingPinId(null), 500);
  };

  // Function to open the share dialog
  const handleOpenShareDialog = (list) => {
    setSelectedList(list);
    setShareDialogOpen(true);
  };

  // Function to close the share dialog
  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setSelectedList(null);
  };

  // Function to open the color picker dialog
  const handleOpenColorDialog = (list) => {
    setSelectedList(list);
    setColorDialogOpen(true);
  };

  // Function to close the color picker dialog
  const handleCloseColorDialog = () => {
    setColorDialogOpen(false);
    setSelectedList(null);
  };

  // Function to update accent color (stored locally)
  const handleUpdateAccentColor = (color) => {
    if (!selectedList) return;
    
    const newColors = {
      ...accentColors,
      [selectedList.list_id]: color,
    };
    
    // Remove null colors to keep storage clean
    if (!color) {
      delete newColors[selectedList.list_id];
    }
    
    setAccentColors(newColors);
    localStorage.setItem(ACCENT_COLORS_KEY, JSON.stringify(newColors));
    handleCloseColorDialog();
  };
  
  // Get accent color for a list (from local storage)
  const getListAccentColor = (listId) => accentColors[listId] || null;

  // Function to toggle sharing of a list
  const handleToggleShare = async () => {
    const listId = selectedList.list_id;
    const currentSharedStatus = selectedList.shared_with_household;

    const { error } = await supabase
      .from('saved_lists')
      .update({ shared_with_household: !currentSharedStatus })
      .eq('list_id', listId)
      .eq('user_id', user.id); // Ensure only the owner can update

    if (error) {
      console.error('Error updating shared status:', error);
    } else {
      // Update the list in ownedLists using onUpdateList
      onUpdateList({
        ...selectedList,
        shared_with_household: !currentSharedStatus,
      });
      handleCloseShareDialog();
    }
  };

  return (
    <Grid>
      {/* Search Bar */}
      <Box sx={{ marginTop: theme.spacing(2) }}>
        <TextField
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1); // Reset pagination when search term changes
          }}
          placeholder="search lists..."
          variant="outlined"
          fullWidth
          sx={{
            marginBottom: theme.spacing(2),
            fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px',
              fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
              fontSize: '0.9rem',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(30, 30, 35, 0.8) 0%, rgba(20, 20, 25, 0.9) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
              transition: 'all 0.2s ease',
              '& fieldset': {
                borderColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.1)' 
                  : 'rgba(0,0,0,0.08)',
                borderWidth: '1.5px',
              },
              '&:hover fieldset': {
                borderColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.2)' 
                  : 'rgba(0,0,0,0.15)',
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.mode === 'dark' ? '#BB86FC' : '#6366f1',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 0 0 3px rgba(187, 134, 252, 0.15)'
                  : '0 0 0 3px rgba(99, 102, 241, 0.1)',
              },
            },
            '& .MuiInputBase-input::placeholder': {
              opacity: 0.5,
              fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
            },
          }}
        />
      </Box>

      {/* Tabs for list categories */}
      {!hasSearchTerm && (
        <Box sx={{ marginTop: theme.spacing(2) }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              '& .MuiTabs-indicator': {
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(90deg, #BB86FC, #03DAC6)'
                  : 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                height: '3px',
                borderRadius: '3px',
              },
              '& .MuiTab-root': {
                fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                fontSize: '0.85rem',
                fontWeight: 500,
                letterSpacing: '-0.01em',
                textTransform: 'lowercase',
                minHeight: '48px',
                transition: 'color 0.2s ease',
                '&.Mui-selected': {
                  color: theme.palette.mode === 'dark' ? '#BB86FC' : '#6366f1',
                },
              },
            }}
          >
            <Tab label="my lists" />
            {sharedLists.length > 0 && <Tab label="shared with me" />}
          </Tabs>
        </Box>
      )}

      {/* Display Lists */}
      <Box sx={{ marginTop: theme.spacing(2) }}>
        {currentLists.length > 0 ? (
          <Grid container spacing={2}>
            {currentLists.map((list, index) => {
              const listAccentColor = getListAccentColor(list.list_id);
              const isOwner = list.user_id === user.id;
              const canCustomize = isOwner && list.list_name.toLowerCase() !== 'favorites';
              
              return (
              <Grid item xs={12} sm={6} md={4} key={list.list_id}>
                <Link
                  to={`/lists/${list.list_id}`}
                  style={{ textDecoration: 'none', display: 'block' }}
                >
                <Box
                  sx={{
                    position: 'relative',
                    border: `1.5px solid ${
                      theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.08)' 
                        : 'rgba(0,0,0,0.06)'
                    }`,
                    borderLeft: listAccentColor 
                      ? `3px solid ${listAccentColor}` 
                      : `1.5px solid ${
                          theme.palette.mode === 'dark' 
                            ? 'rgba(255,255,255,0.08)' 
                            : 'rgba(0,0,0,0.06)'
                        }`,
                    padding: '16px 18px',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(30, 30, 35, 0.95) 0%, rgba(20, 20, 25, 0.98) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
                    boxShadow: `0 2px 12px -4px rgba(0,0,0,${
                      theme.palette.mode === 'dark' ? '0.3' : '0.08'
                    })`,
                    '&:hover': {
                      borderColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.15)' 
                        : 'rgba(0,0,0,0.12)',
                      borderLeftColor: listAccentColor || (theme.palette.mode === 'dark' ? '#BB86FC' : '#6366f1'),
                      boxShadow: `0 4px 20px -4px rgba(0,0,0,${
                        theme.palette.mode === 'dark' ? '0.4' : '0.12'
                      })`,
                      transform: 'translateY(-2px)',
                    },
                    '@media (min-width: 600px)': {
                      '&:hover .action-icons': {
                        opacity: 1,
                      },
                      '&:hover .pin-indicator': {
                        opacity: 0,
                      },
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    // Staggered entrance animation
                    opacity: 0,
                    animation: isInitialLoad 
                      ? `${fadeSlideIn} 0.35s ease-out forwards`
                      : 'none',
                    animationDelay: isInitialLoad ? `${index * 40}ms` : '0ms',
                    ...(!isInitialLoad && { opacity: 1 }),
                  }}
                >
                  {/* Pinned indicator - always visible when pinned, hides on hover (desktop) */}
                  {list.pinned && canCustomize && (
                    <Box
                      className="pin-indicator"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 1,
                        opacity: { xs: 0, sm: 1 },
                        transition: 'opacity 0.15s ease',
                  }}
                >
                      <PushPinIcon sx={{ fontSize: 16, color: theme.palette.text.disabled }} />
                    </Box>
                  )}
                  
                  {/* Action icons - absolutely positioned so they don't affect layout */}
                  {canCustomize && (
                    <Box 
                      className="action-icons"
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                      display: 'flex',
                      alignItems: 'center',
                        gap: '4px',
                        opacity: { xs: 1, sm: 0 },
                        transition: 'opacity 0.15s ease',
                        zIndex: 2,
                    }}
                  >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleOpenColorDialog(list);
                        }}
                        sx={{
                          padding: '4px',
                          color: listAccentColor || theme.palette.text.secondary,
                          '&:hover': { color: listAccentColor || theme.palette.primary.main },
                        }}
                      >
                        <PaletteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleOpenShareDialog(list);
                        }}
                            sx={{
                          padding: '4px',
                              color: theme.palette.text.secondary,
                          '&:hover': { color: theme.palette.primary.main },
                        }}
                              >
                        <ShareIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                              <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handlePinClick(list.list_id, list.pinned);
                        }}
                        sx={{
                          padding: '4px',
                          color: list.pinned ? theme.palette.primary.main : theme.palette.text.secondary,
                          animation: animatingPinId === list.list_id 
                            ? `${pinBounce} 0.5s ease-out` 
                            : 'none',
                          '&:hover': { color: theme.palette.primary.main },
                        }}
                              >
                        {list.pinned ? <PushPinIcon sx={{ fontSize: 18 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 18 }} />}
                              </IconButton>
                    </Box>
                          )}
                  
                  {/* Owner avatar for shared lists - also absolute */}
                  {!isOwner && list.profiles && (
                          <Avatar
                            src={list.profiles.avatar_url || ''}
                      alt={list.profiles.first_name || 'User'}
                      sx={{ 
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        width: 28, 
                        height: 28,
                        zIndex: 2,
                      }}
                    />
                  )}

                  {/* Title */}
                  <Typography
                    sx={{
                      fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      lineHeight: 1.3,
                      letterSpacing: '-0.02em',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      pr: canCustomize || (!isOwner && list.profiles) ? 8 : 0,
                    }}
                  >
                    {list.list_name}
                    {list.shared_with_household && isOwner && (
                      <Typography
                        component="span"
                        sx={{
                          color: theme.palette.mode === 'dark' ? '#03DAC6' : '#10b981',
                          fontSize: '10px',
                          fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                          ml: 0.75,
                          fontWeight: 500,
                        }}
                      >
                        · shared
                      </Typography>
                      )}
                  </Typography>
                  
                  {/* Footer: Thumbnails + Count */}
                  <ThumbnailStrip 
                    thumbnails={list.thumbnails} 
                    recipeCount={list.recipeCount}
                    theme={theme} 
                  />
                </Box>
                </Link>
              </Grid>
            );
            })}
          </Grid>
        ) : hasSearchTerm ? (
          <NoSearchResultsState searchTerm={searchTerm} />
        ) : ownedLists.length === 0 && tabValue === 0 ? (
          <EmptyListsState onCreateList={() => {
            // Focus on the new list input
            const input = document.querySelector('input[placeholder="new list"]');
            if (input) input.focus();
          }} />
        ) : (
          <Typography>No lists found.</Typography>
        )}

        {/* Pagination Component */}
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            sx={{ marginTop: theme.spacing(2), display: 'flex', justifyContent: 'center' }}
          />
        )}
      </Box>

      {/* New list creation UI - Moved to bottom of "My Lists" */}
      {!hasSearchTerm && tabValue === 0 && (
        <Box sx={{ marginTop: theme.spacing(4) }}>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: theme.spacing(1),
              marginTop: theme.spacing(1),
            }}
          >
            <TextField
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCreateList();
                }
              }}
              placeholder="new list"
              variant="outlined"
              fullWidth
              sx={{
                fontFamily: theme.typography.fontFamily,
                '& .MuiOutlinedInput-root': {
                  fontFamily: theme.typography.fontFamily,
                  height: '3em',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  '&:focus-within': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: theme.spacing(2),
                },
              }}
              InputProps={{
                style: {
                  height: '3em',
                },
              }}
            />

            <Button
              variant="outlined"
              onClick={handleCreateList}
              disabled={!newListName.trim()}
              sx={{
                minWidth: '3em',
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.text.secondary,
                backgroundColor: theme.palette.background.paper,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  transform: 'scale(1.05)',
                  borderColor: theme.palette.primary.main,
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
                '&.Mui-disabled': {
                  opacity: 0.5,
                },
              }}
            >
              <AddIcon />
            </Button>
          </Box>
        </Box>
      )}

      {/* Share Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={handleCloseShareDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {selectedList?.shared_with_household ? 'stop sharing' : 'share list'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedList &&
              (selectedList.shared_with_household
                ? `this list is currently shared with your household. do you want to stop sharing it?`
                : `do you want to share "${selectedList.list_name}" with your household?`)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseShareDialog}
            sx={{ color: 'text.secondary' }}
          >
            cancel
          </Button>
          <Button 
            onClick={handleToggleShare} 
            variant="contained"
            color={selectedList?.shared_with_household ? "error" : "primary"}
            sx={{
              fontWeight: 600,
              ...(selectedList?.shared_with_household && {
                backgroundColor: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: theme.palette.error.dark,
                },
              }),
            }}
          >
            {selectedList?.shared_with_household ? 'stop sharing' : 'share'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Color Picker Dialog */}
      <Dialog 
        open={colorDialogOpen} 
        onClose={handleCloseColorDialog}
        PaperProps={{
          sx: {
            minWidth: '340px',
          }
        }}
      >
        <DialogTitle>choose color</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: 1.5,
              py: 1,
              justifyItems: 'center',
            }}
          >
            {ACCENT_COLORS.map((color, index) => {
              const isDark = theme.palette.mode === 'dark';
              const isSelected = selectedList && getListAccentColor(selectedList.list_id) === color;
              
              return (
                <Box
                  key={index}
                  onClick={() => handleUpdateAccentColor(color)}
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    backgroundColor: color || (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
                    border: color 
                      ? `2px solid ${color}` 
                      : `2px dashed ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}`,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    '&:hover': {
                      transform: 'scale(1.12) translateY(-2px)',
                      boxShadow: color 
                        ? `0 4px 12px -2px ${color}60`
                        : `0 4px 12px -2px rgba(0,0,0,0.2)`,
                    },
                    ...(isSelected && {
                      transform: 'scale(1.1)',
                      boxShadow: `0 0 0 3px ${isDark ? 'rgba(30,30,35,1)' : 'rgba(255,255,255,1)'}, 0 0 0 5px ${color || theme.palette.primary.main}`,
                    }),
                  }}
                >
                  {!color && (
                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.disabled, fontWeight: 500 }}>
                      ✕
                    </Typography>
                  )}
                  {isSelected && color && (
                    <Box
                      sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '14px',
                        fontWeight: 700,
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      }}
                    >
                      ✓
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>
    </Grid>
  );
}

export default ListOverview;
